import { Component, computed, inject, input, output } from '@angular/core';
import { RouterLinkWithHref } from '@angular/router';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_CARD, KENDO_EXPANSIONPANEL, KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { KENDO_POPUP } from '@progress/kendo-angular-popup';
import { IconService } from '@unity/services/icon.service';
import { PermissionService } from '@unity/services/permission.service';
import { QuickActionProps } from '@unity/utils/types';

@Component({
  selector: 'unity-action-shared',
  imports: [KENDO_POPUP, KENDO_CARD, KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT, KENDO_EXPANSIONPANEL, IconsModule, RouterLinkWithHref],
  templateUrl: './action-shared.component.html',
  styleUrl: './action-shared.component.scss',
})
export class ActionSharedComponent<TInput extends QuickActionProps> {
  private readonly iconService = inject(IconService);
  private readonly permissionService = inject(PermissionService);

  protected readonly $actionIcon = computed(() => {
    const action = this.$action();
    return this.iconService.getSVGIcon(action.icon);
  });

  protected readonly $queryParams = computed(() => {
    const action = this.$action();
    return action.query ?? {};
  });

  protected readonly $authorized = computed(() => {
    const action = this.$action();

    if (action.authorize === undefined) {
      return true;
    }

    if (action.authorizeOneOf !== undefined && !this.permissionService.userIsEntitledToOneOf(action.authorizeOneOf)) {
      return false;
    }

    return this.permissionService.userIsEntitled(action.authorize);
  });

  public readonly $action = input.required<TInput>({ alias: 'action' });
  public readonly $disabled = input<boolean>(false, { alias: 'disabled' });
  public readonly $gridIconOnly = input<boolean>(false, { alias: 'gridIconOnly' });
  protected readonly $buttonClasses = computed(() => (this.$gridIconOnly() ? 'k-link action-button-icon-only' : 'k-link action-button'));

  public readonly clickEvent = output();

  protected onClick(event: Event) {
    const disabled = this.$disabled();
    if (disabled) {
      event.preventDefault();
      return;
    }
    const action = this.$action();
    if (action.callback) {
      this.clickEvent.emit();
      action.callback();
    } else {
      this.clickEvent.emit();
    }
  }
}
